import { useBottomTabBarHeightOrDefault } from 'app/hooks/navigation';
import {
  type ReactNode,
  type RefObject,
  useRef,
  useState,
  useCallback,
  useMemo,
  memo,
  type ComponentProps,
} from 'react';
import { FlatList, KeyboardAvoidingView, Platform, View, type ViewToken } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useHeaderHeight } from '@react-navigation/elements';
import { useScrollToTop } from '@react-navigation/native';
import { FlashList, type FlashListProps, type FlashListRef } from '@shopify/flash-list';
import { useTheme } from '@shopify/restyle';
import { Column } from '../box';
import type { ViewStyle } from 'react-native/Libraries/StyleSheet/StyleSheetTypes';
import { useKeyboard } from '@react-native-community/hooks';
import { isMobileNative } from 'app/utils/web-helpers';
import { useDeviceHeight } from 'app/hooks/use-device-size';
import type { Theme } from 'app/shopify';
import { PageListComponentType } from './list.constants';

const PageListComponent = {
  [PageListComponentType.FLASH]: FlashList,
  [PageListComponentType.FLAT]: FlatList,
};

export type FlashOrFlatList<T, U = never> = (FlashListRef<T> & FlatList<T>) | U;

export interface ListPageProps<T, U = FlashOrFlatList<T>> extends ComponentProps<
  typeof FlashList<T> & typeof FlatList<T>
> {
  accessory?: ReactNode;
  innerRef?: RefObject<U | null>;
  keyboardAvoidingEnabled?: boolean;
  listType?: keyof typeof PageListComponent;
  refreshing?: boolean;
  style?: ViewStyle;
  withPadding?: boolean;
  inverted?: boolean;
}

const typedMemo: <T>(c: T, e?: (prev: object, next: object) => boolean) => T = memo;

const ListPage = typedMemo(function ListPageComponent<T>({
  accessory,
  innerRef,
  keyboardAvoidingEnabled = true,
  listType = PageListComponentType.FLAT,
  ListHeaderComponentStyle,
  ListFooterComponentStyle,
  refreshing,
  style,
  numColumns,
  withPadding = true,
  contentContainerStyle: callerContentContainerStyle,
  ...rest
}: ListPageProps<T>) {
  const theme = useTheme<Theme>();
  const insets = useSafeAreaInsets();
  const headerHeight = useHeaderHeight();
  const tabBarHeight = useBottomTabBarHeightOrDefault();
  const [accessoryHeight, setAccessoryHeight] = useState(0);
  const deviceHeight = useDeviceHeight();
  const [pageHeight, setPageHeight] = useState(0);

  const flashListKey =
    numColumns === undefined ? 'page-list-single-column-flow' : `page-list-grid-${numColumns.toString()}`;

  const listRef = useRef<FlashOrFlatList<T>>(null);

  innerRef ??= listRef;

  useScrollToTop(innerRef);

  const [isFirstItemVisible, setIsFirstItemVisible] = useState(false);
  const handleViewableItemsChanged = useCallback(({ viewableItems }: { viewableItems: ViewToken[] }) => {
    setIsFirstItemVisible(viewableItems.length === 0 || viewableItems.some(({ index }) => index === 0));
  }, []);

  const keyboard = useKeyboard();

  const accessoryContainer = useRef<View>(null);
  const onAccessoryLayout = () => {
    if (!accessoryContainer.current) return;
    accessoryContainer.current.measure((_x, _y, _width, height, _pageX, _pageY) => {
      setAccessoryHeight((currentHeight) => (currentHeight === height ? currentHeight : height));
    });
  };

  const spacingX8 = theme.spacing['spacing.general.x8'];
  const spacingX3 = theme.spacing['spacing.general.x3'];

  const offset = useMemo(() => {
    let offsetCalc = tabBarHeight + accessoryHeight;

    if (isMobileNative && tabBarHeight <= 0 && pageHeight > 0 && accessoryHeight > 0) {
      offsetCalc += deviceHeight - headerHeight - pageHeight;
      if (Platform.OS === 'ios') {
        offsetCalc -= spacingX8 + spacingX3;
      }
    }

    return offsetCalc;
  }, [accessoryHeight, deviceHeight, headerHeight, pageHeight, tabBarHeight, spacingX8, spacingX3]);

  const ListComponent = PageListComponent[listType];
  const horizontalPadding = getListHorizontalPadding(withPadding, rest, insets, theme);
  const listStyleProps = {
    style,
    contentContainerStyle: [horizontalPadding, callerContentContainerStyle],
  };

  if (rest.inverted && listType === PageListComponentType.FLASH) {
    rest.data = Array.from(rest.data ?? []).reverse();
    (rest as unknown as FlashListProps<T>).maintainVisibleContentPosition = {
      autoscrollToBottomThreshold: 0.2,
      startRenderingFromBottom: true,
    };
  }

  return (
    <KeyboardAvoidingView
      behavior={
        keyboardAvoidingEnabled && (Platform.OS !== 'android' || keyboard.keyboardShown) ? 'padding' : undefined
      }
      enabled={keyboardAvoidingEnabled}
      keyboardVerticalOffset={offset}
      onLayout={(event) => {
        const height = event.nativeEvent.layout.height;
        if (height !== pageHeight) setPageHeight(height);
      }}
      style={{
        flex: 1,
        minHeight: 0,
        justifyContent: 'space-between',
      }}
    >
      <Column flex={1} minHeight={0}>
        <ListComponent
          // masonry={!!numColumns}
          numColumns={numColumns}
          key={flashListKey}
          ItemSeparatorComponent={null}
          ListFooterComponent={(rest.ListFooterComponent ?? rest.inverted) ? <View /> : null}
          ListFooterComponentStyle={[
            {
              paddingBottom: (() => {
                if (rest.inverted) {
                  if (headerHeight > 0) return theme.spacing['spacing.general.x8'];
                  if (rest.ListFooterComponent) return insets.top || theme.spacing['spacing.general.x8'];
                  return theme.spacing['spacing.general.x4'];
                }

                if (accessory) return theme.spacing['spacing.general.x4'];
                return insets.bottom || theme.spacing['spacing.general.x4'];
              })(),
            },
            ListFooterComponentStyle,
          ]}
          ListHeaderComponent={(rest.ListHeaderComponent ?? !rest.inverted) ? <View /> : null}
          ListHeaderComponentStyle={[
            {
              paddingTop: (() => {
                if (rest.inverted) {
                  if (headerHeight > 0) return theme.spacing['spacing.general.x8'];
                  if (rest.ListHeaderComponent) return insets.top || theme.spacing['spacing.general.x8'];
                  return theme.spacing['spacing.general.x2'];
                }

                return theme.spacing['spacing.general.x2'];
              })(),
            },
            ListHeaderComponentStyle,
          ]}
          keyboardShouldPersistTaps="handled"
          onViewableItemsChanged={handleViewableItemsChanged}
          ref={innerRef}
          refreshing={refreshing ? isFirstItemVisible : false}
          removeClippedSubviews={false}
          showsVerticalScrollIndicator={false}
          viewabilityConfig={{ itemVisiblePercentThreshold: 50 }}
          {...listStyleProps}
          {...rest}
          {...getGridColumnStyle(numColumns, theme, rest)}
        />
      </Column>

      {accessory ? (
        <Column onLayout={onAccessoryLayout} innerRef={accessoryContainer}>
          {accessory}
        </Column>
      ) : null}
    </KeyboardAvoidingView>
  );
});

export { ListPage };

function getGridColumnStyle(
  numColumns: number | undefined,
  theme: Theme,
  rest: Pick<ListPageProps<unknown>, 'horizontal' | 'columnWrapperStyle'>,
) {
  return numColumns && numColumns > 1
    ? {
        columnWrapperStyle: [
          {
            columnGap: theme.spacing['spacing.general.x4'],
            alignItems: 'flex-start' as const,
            justifyContent: 'center' as const,
          },
          rest.columnWrapperStyle,
        ],
      }
    : {};
}

function getListHorizontalPadding(
  withPadding: boolean,
  rest: Pick<ListPageProps<unknown>, 'horizontal' | 'columnWrapperStyle'>,
  insets: ReturnType<typeof useSafeAreaInsets>,
  theme: Theme,
) {
  const shouldApplyHorizontalPadding = withPadding && !rest.horizontal;
  const horizontalPadding = {
    paddingLeft: shouldApplyHorizontalPadding ? insets.left || theme.spacing['spacing.general.x4'] : 0,
    paddingRight: shouldApplyHorizontalPadding ? insets.right || theme.spacing['spacing.general.x4'] : 0,
  };
  return horizontalPadding;
}
